// Swiss Post Postcard API Types
// Based on: https://developer.post.ch/en/technical-specifications-of-postcard-api

export interface SenderAddress {
  lastname: string
  firstname: string
  company?: string
  street: string
  houseNr?: string
  zip: string
  city: string
}

export interface RecipientAddress {
  title?: string
  lastname: string
  firstname: string
  company?: string
  street: string
  houseNr?: string
  zip: string
  city: string
  country?: string
  poBox?: string
  additionalAdrInfo?: string
}

export interface BrandingText {
  text: string
  blockColor?: string
  textColor?: string
}

export interface BrandingQRCode {
  encodedText: string
  accompanyingText?: string
  blockColor?: string
  textColor?: string
}

export interface PostcardBranding {
  brandingText?: BrandingText
  brandingQRCode?: BrandingQRCode
}

export interface CreatePostcardRequest {
  recipientAddress: RecipientAddress
  senderAddress?: SenderAddress
  senderText?: string
  branding?: PostcardBranding
}

// Legacy type alias for backward compatibility
export type PostcardAddress = RecipientAddress

export interface PostcardResponse {
  cardKey: string
  recipientAddress: RecipientAddress
  senderAddress?: SenderAddress
  senderText?: string
  frontImageUrl?: string
  brandingImageUrl?: string
  stampImageUrl?: string
  branding?: PostcardBranding
  approved: boolean
  created: string
  printed?: string
  warnings?: ApiWarning[]
  errors?: ApiError[]
}

export interface CampaignStatsResponse {
  campaignKey: string
  quota: number
  sendPostcards: number
  freeToSendPostcards: number
}

// Legacy interfaces for backward compatibility
export interface BrandingTextRequest extends BrandingText {
  backgroundColor?: string // Legacy field name
}

export type BrandingQrRequest = BrandingQRCode

export interface PostcardApprovalRequest {
  approved: boolean
}

export interface OAuthTokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  scope: string
}

export interface ApiError {
  code: number
  message?: string
  description?: string
  details?: string
}

export interface PostcardPreviewResponse {
  cardKey: string
  fileType: string
  encoding: string
  side: string
  imagedata: string
  errors?: ApiError[]
  warnings?: ApiWarning[]
}

export interface ApiWarning {
  code: number
  message?: string
  description?: string
  details?: string
}

export interface ApiIssueCollection {
  errors?: ApiError[] | null
  warnings?: ApiWarning[] | null
}

export type ApiResult<T> = T & ApiIssueCollection

// Response wrapper for API calls
export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
  code?: number
  details?: string
}

// Image upload types
export interface ImageUploadRequest {
  cardKey: string
  image: File | Buffer
  contentType: string
}

// Supported image formats
export type SupportedImageFormat = 'image/jpeg' | 'image/png'

// Postcard dimensions (in pixels at 300dpi)
export const POSTCARD_DIMENSIONS = {
  FRONT_IMAGE: {
    width: 1819,
    height: 1311,
  },
  STAMP_IMAGE: {
    width: 343,
    height: 248,
  },
  BRANDING_IMAGE: {
    width: 777,
    height: 295,
  },
} as const

// Text field limits
export const TEXT_LIMITS = {
  SENDER_TEXT: 400,
  SENDER_ADDRESS: 45,
  SENDER_ADDRESS_FIRSTNAME: 75,
  SENDER_ADDRESS_LASTNAME: 75,
  SENDER_ADDRESS_COMPANY: 39,
  SENDER_ADDRESS_STREET: 50,
  SENDER_ADDRESS_HOUSENR: 5,
  SENDER_ADDRESS_ZIP: 39,
  SENDER_ADDRESS_CITY: 30,
  RECIPIENT_ADDRESS_TITLE: 30,
  RECIPIENT_ADDRESS_FIRSTNAME: 75,
  RECIPIENT_ADDRESS_LASTNAME: 75,
  RECIPIENT_ADDRESS_COMPANY: 39,
  RECIPIENT_ADDRESS_STREET: 50,
  RECIPIENT_ADDRESS_HOUSENR: 5,
  RECIPIENT_ADDRESS_ZIP: 39,
  RECIPIENT_ADDRESS_CITY: 30,
  RECIPIENT_ADDRESS_POBOX: 5,
  RECIPIENT_ADDRESS_ADDITIONAL_ADR_INFO: 75,
  BRANDING_TEXT_TEXT: 250,
  BRANDING_TEXT_TEXTCOLOR: 7,
  BRANDING_TEXT_BLOCK_COLOR: 7,
  BRANDING_QR_ENCODED_TEXT: 100,
  BRANDING_QR_ACCOMPANYING_TEXT: 250,
  BRANDING_QR_TEXTCOLOR: 7,
  BRANDING_QR_BLOCK_COLOR: 7,
} as const

// Error codes from Swiss Post API
export enum PostcardErrorCode {
  // Campaign errors
  CAMPAIGN_QUOTA_EXCEEDED = 2000,
  CAMPAIGN_EXPIRED = 2010,
  CAMPAIGN_NOT_STARTED = 2020,
  CAMPAIGN_NOT_ACTIVE = 2030,
  CAMPAIGN_NOT_FOUND = 4000,
  CAMPAIGN_CONFIG_NOT_FOUND = 4001,

  // Postcard errors
  BRANDING_NOT_FOUND = 4002,
  POSTCARD_NOT_FOUND = 4003,
  POSTCARD_ALREADY_APPROVED = 6000,

  // Validation errors
  NAMES_COMBINATION_NOT_ALLOWED = 1200,

  // File format errors
  FILE_FORMAT_NOT_SUPPORTED = 5010,
  BAD_RESOLUTION = 5020,

  // System errors
  PERIPHERAL_SYSTEM_NOT_AVAILABLE = 5050,
  ENCODING_VIOLATION = 5000,
}

// Warning codes
export enum PostcardWarningCode {
  BAD_RESOLUTION = 5020,
  ENCODING_VIOLATION = 5000,
}

// Utility type for creating valid hex colors
export type HexColor = `#${string}`

// Postcard state enum
export enum PostcardState {
  CREATED = 'created',
  IMAGE_UPLOADED = 'image_uploaded',
  BRANDING_SET = 'branding_set',
  APPROVED = 'approved',
  PRINTED = 'printed',
  DELIVERED = 'delivered',
}

export interface PostcardDesignOption {
  id: string
  text?: string
  /** Postcard print image URL (Swiss Post upload). */
  imageUrl: string
  imageAlt?: string
  /** Design picker thumbnail URL. */
  previewImageUrl?: string
  previewImageAlt?: string
}

export interface CampaignData {
  /** Selected postcard storage entity ID. */
  design: string
  receiving: {
    firstname: string
    lastname: string
    company?: string
    street: string
    number: string
    city: string
    postalCode: string
  }
  message: string
  sender: {
    firstname: string
    lastname: string
    street: string
    number: string
    city: string
    postalCode: string
    email: string
  }
  consentPrivacy: boolean
  consentMarketing: boolean
  consentContest: boolean
  branding?: PostcardBranding
}

export interface PostcardStep {
  name: string
  title: string
  description: string
  buttonText?: string
  buttonTextBackward?: string
  buttonTextForward?: string
  onNext?: () => Promise<boolean>
}
